import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Star, Quote, Loader2 } from 'lucide-react';
import { useCMS } from '../hooks/useCMS';
import { useSchemaOrg } from '../hooks/useSchemaOrg';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ReviewsPublic = () => {
  const { get } = useCMS('reviews');
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/reviews?published_only=true&featured_only=true&limit=6`),
        axios.get(`${API}/reviews/stats`)
      ]);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Fallback to empty array if API fails
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate aggregate rating from stats or reviews
  const averageRating = stats?.average_rating?.toFixed(1) || '5.0';
  const totalReviews = stats?.total_reviews || reviews.length;

  // Generate Review schema for each review
  const reviewSchemas = reviews.map(review => ({
    "@type": "Review",
    "author": {
      "@type": "Person",
      "name": review.author
    },
    "datePublished": review.date_published || new Date().toISOString().split('T')[0],
    "reviewBody": review.text,
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": review.rating,
      "bestRating": "5"
    }
  }));

  const { name: companyName, publisher, geo, logoUrl } = useSchemaOrg();

  // Generate LocalBusiness schema with aggregate rating
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "name": companyName,
    ...(logoUrl ? { "image": logoUrl } : {}),
    "address": publisher.address,
    ...(geo ? { "geo": geo } : {}),
    "telephone": publisher.telephone,
    "priceRange": "$$",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": totalReviews,
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": reviewSchemas
  };

  // Avatar component with fallback
  const Avatar = ({ author, avatarUrl }) => {
    const [imageError, setImageError] = useState(false);
    
    if (avatarUrl && !imageError) {
      return (
        <img 
          src={avatarUrl}
          alt={author}
          className="w-10 h-10 rounded-full object-cover border-2 border-gray-600"
          onError={() => setImageError(true)}
        />
      );
    }
    
    return (
      <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-green-800 rounded-full flex items-center justify-center font-bold text-white">
        {author.charAt(0)}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      
      <section id="reviews" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden" data-testid="reviews-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="checkered-bg w-full h-full"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <Badge className="bg-green-600/20 text-green-400 border-green-600/50 mb-4 px-4 py-1">
              {get('reviews_badge', 'Anmeldelser')}
            </Badge>
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              <span className="text-white">{get('reviews_title_part1', 'Hva Kundene')}</span>{' '}
              <span className="bg-gradient-to-r from-green-400 to-yellow-400 bg-clip-text text-transparent">{get('reviews_title_part2', 'Sier')}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {get('reviews_subtitle', 'Vi er stolte av vår 5-stjerners vurdering på Google')}
            </p>
          </div>

          {/* Google Rating Banner */}
          <div className="max-w-md mx-auto mb-12">
            <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <div className="text-4xl font-black text-white mb-2" data-testid="average-rating">
                {averageRating}
              </div>
              <div className="text-gray-400">
                {get('reviews_google_text', 'Basert på Google Anmeldelser')}
                {totalReviews > 0 && (
                  <span className="block text-sm mt-1">({totalReviews} anmeldelser)</span>
                )}
              </div>
              <a
                href={get('reviews_google_link', 'https://www.google.com/search?client=ms-android-motorola-rvo3&sca_esv=747b26111898c262&sxsrf=AE3TifPjdSAY5X_WAfBWejDQygeXHwFFNw:1763334295174&q=sand-tec+as+anmeldelser')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-red-400 hover:text-red-300 font-semibold transition-colors duration-300"
              >
                {get('reviews_google_cta', 'Se alle anmeldelser →')}
              </a>
            </div>
          </div>

          {/* Reviews Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            </div>
          ) : reviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-testid="reviews-grid">
              {reviews.map((review, index) => (
                <Card
                  key={review.id}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 backdrop-blur-sm hover:border-green-600/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-green-500/20 group"
                  style={{ animationDelay: `${index * 0.15}s` }}
                  data-testid={`review-card-${index}`}
                >
                  <CardContent className="pt-6">
                    {/* Quote Icon */}
                    <Quote className="w-10 h-10 text-red-600/30 mb-4" />

                    {/* Stars */}
                    <div className="flex mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>

                    {/* Review Text */}
                    <p className="text-gray-300 mb-6 leading-relaxed">
                      "{review.text}"
                    </p>

                    {/* Author */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                      <div>
                        <div className="font-bold text-white group-hover:text-green-400 transition-colors duration-300">
                          {review.author}
                        </div>
                        <div className="text-sm text-gray-500">{review.date_display}</div>
                      </div>
                      <Avatar author={review.author} avatarUrl={review.avatar_url} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400">Ingen anmeldelser tilgjengelig ennå.</p>
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-gray-400 text-lg">{get('reviews_cta', 'Bli en av våre fornøyde kunder')}</p>
          </div>
        </div>
      </section>
    </>
  );
};

export default ReviewsPublic;
